import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { gaps } from "../../../primitives/spacing";
import { AuthFormFrame } from "../AuthLayout";
import { ROUTES } from "../../../../route_paths";

interface ResetPasswordFormProps {
  password: string;
  setPassword: (v: string) => void;
  confirm: string;
  setConfirm: (v: string) => void;
  loading: boolean;
  error: string | null;
  invalidLink: boolean;
  onSubmit: (e: React.FormEvent) => Promise<void>;
}

/** Shared reset-password form. Renders an invalid-link state when the recovery token is absent. */
export function ResetPasswordForm({
  password,
  setPassword,
  confirm,
  setConfirm,
  loading,
  error,
  invalidLink,
  onSubmit,
}: ResetPasswordFormProps) {
  const { t } = useTranslation();

  if (invalidLink) {
    return (
      <AuthFormFrame>
        <Alert severity="error">{t("auth.resetPasswordPage.invalidLink")}</Alert>
        <Button component={Link} to={ROUTES.forgotPassword.path()} variant="contained">
          {t("auth.resetPasswordPage.requestNew")}
        </Button>
      </AuthFormFrame>
    );
  }

  return (
    <AuthFormFrame
      title={t("auth.resetPasswordPage.title")}
      subtitle={t("auth.resetPasswordPage.subtitle")}
    >
      {error && <Alert severity="error">{error}</Alert>}
      <Stack component="form" onSubmit={onSubmit} gap={gaps.base}>
        <TextField
          label={t("auth.resetPasswordPage.newPassword")}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          fullWidth
          autoFocus
          autoComplete="new-password"
          size="small"
        />
        <TextField
          label={t("auth.resetPasswordPage.confirm")}
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
          fullWidth
          autoComplete="new-password"
          size="small"
        />
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={loading || !password || !confirm}
        >
          {loading ? t("auth.resetPasswordPage.saving") : t("auth.resetPasswordPage.save")}
        </Button>
      </Stack>
    </AuthFormFrame>
  );
}
